import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Shield, Download, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function Privacy() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/export-data`, { 
        withCredentials: true,
        responseType: 'json'
      });
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chronos-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('privacy.exportSuccess'));
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE_MY_ACCOUNT') {
      toast.error(t('privacy.confirmRequired'));
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/gdpr/delete-account?password_confirm=${deleteConfirm}`, { 
        withCredentials: true 
      });
      
      toast.success(t('privacy.accountDeleted'));
      
      // Logout and redirect to home
      setTimeout(async () => {
        await logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('privacy.title')}</h1>
        </div>

        <p className="text-gray-600 dark:text-gray-300">{t('privacy.subtitle')}</p>

        {/* Export Data */}
        <Card className="p-6 dark:bg-gray-800">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-100 dark:bg-teal-900 rounded-full">
              <Download className="h-6 w-6 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('privacy.exportData')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{t('privacy.exportDescription')}</p>
              <Button
                onClick={handleExportData}
                className="bg-teal-500 hover:bg-teal-600 text-white gap-2"
              >
                <Download className="h-4 w-4" />
                {t('privacy.downloadData')}
              </Button>
            </div>
          </div>
        </Card>

        {/* GDPR Rights */}
        <Card className="p-6 dark:bg-gray-800">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('privacy.yourRights')}</h3>
          <div className="space-y-3 text-sm">
            <div className="flex gap-3">
              <span className="text-teal-500">✓</span>
              <p className="text-gray-600 dark:text-gray-300">{t('privacy.right1')}</p>
            </div>
            <div className="flex gap-3">
              <span className="text-teal-500">✓</span>
              <p className="text-gray-600 dark:text-gray-300">{t('privacy.right2')}</p>
            </div>
            <div className="flex gap-3">
              <span className="text-teal-500">✓</span>
              <p className="text-gray-600 dark:text-gray-300">{t('privacy.right3')}</p>
            </div>
            <div className="flex gap-3">
              <span className="text-teal-500">✓</span>
              <p className="text-gray-600 dark:text-gray-300">{t('privacy.right4')}</p>
            </div>
          </div>
        </Card>

        {/* Danger Zone - Delete Account */}
        <Card className="p-6 border-2 border-red-200 dark:border-red-900 dark:bg-gray-800">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-full">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">{t('privacy.dangerZone')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{t('privacy.deleteWarning')}</p>
              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="destructive"
                className="bg-red-500 hover:bg-red-600 gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {t('privacy.deleteAccount')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('privacy.confirmDeletion')}
            </DialogTitle>
            <DialogDescription>
              {t('privacy.deletionWarning')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                {t('privacy.cannotUndo')}
              </p>
              <ul className="text-xs text-red-700 dark:text-red-400 mt-2 space-y-1">
                <li>• {t('privacy.willDelete1')}</li>
                <li>• {t('privacy.willDelete2')}</li>
                <li>• {t('privacy.willDelete3')}</li>
              </ul>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block dark:text-white">
                {t('privacy.typeConfirm')}
              </label>
              <Input
                placeholder="DELETE_MY_ACCOUNT"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={loading || deleteConfirm !== 'DELETE_MY_ACCOUNT'}
              variant="destructive"
              className="bg-red-500 hover:bg-red-600"
            >
              {loading ? t('common.processing') : t('privacy.permanentlyDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
